import React, { useEffect, useState } from "react";
import { fmtDateTime } from "@/lib/dateUtils";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, HardDrive, Users as UsersIcon, CreditCard } from "lucide-react";

export default function SubscriptionPage() {
  const { user, refreshMe } = useAuth();
  const [data, setData] = useState({ plans: [], topups: [] });
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(null);
  const [tx, setTx] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/subscriptions/plans");
      setData(data);
      const r = await api.get("/subscriptions/transactions").catch(() => ({ data: { transactions: [] } }));
      setTx(r.data.transactions || []);
    })();
  }, []);

  const checkout = async (kind, code) => {
    setLoading(`${kind}:${code}`);
    try {
      const { data: r } = await api.post("/subscriptions/checkout", {
        kind, code, billing, origin_url: window.location.origin,
      });
      window.location.href = r.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
    finally { setLoading(null); }
  };

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ billing</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter">Plans & top-ups</h1>
        <p className="text-sm text-neutral-600 mt-1">You're on <strong className="capitalize">{user?.plan}</strong> · {user?.ai_credits} AI credits left · {user?.student_limit} student seats.</p>
      </div>

      <div className="inline-flex bg-white border border-neutral-200 p-1">
        <button onClick={() => setBilling("monthly")} data-testid="billing-monthly" className={`px-4 py-2 text-sm font-medium ${billing === "monthly" ? "bg-[#0A0C10] text-white" : ""}`}>Monthly</button>
        <button onClick={() => setBilling("yearly")} data-testid="billing-yearly" className={`px-4 py-2 text-sm font-medium ${billing === "yearly" ? "bg-[#0A0C10] text-white" : ""}`}>Yearly (save 17%)</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {data.plans.map((p) => {
          const price = billing === "yearly" ? p.price_yearly : p.price_monthly;
          const isCurrent = user?.plan === p.code;
          const featured = p.code === "professional";
          return (
            <div key={p.code} data-testid={`sub-plan-${p.code}`} className={`relative ${featured ? "tracing-beam bg-[#002FA7] text-white" : "bg-white border border-neutral-200"} p-6 flex flex-col`}>
              <div className="font-display font-bold text-lg">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display font-bold text-4xl tracking-tighter">${price}</span>
                <span className={`text-sm ${featured ? "text-neutral-300" : "text-neutral-500"}`}>/ {billing}</span>
              </div>
              <ul className={`mt-4 space-y-1.5 text-xs flex-1 ${featured ? "text-neutral-200" : "text-neutral-700"}`}>
                <li>{p.student_limit} students</li>
                <li>{p.ai_credits.toLocaleString()} AI credits</li>
                <li>{Math.round(p.storage_mb / 1024 * 10) / 10} GB storage</li>
                <li>{p.class_limit} classes</li>
                {p.features.map((f) => <li key={f}>· {f}</li>)}
              </ul>
              <button
                disabled={isCurrent || price === 0 || loading === `plan:${p.code}`}
                onClick={() => checkout("plan", p.code)}
                data-testid={`buy-plan-${p.code}`}
                className={`mt-5 inline-flex items-center justify-center gap-2 font-bold py-2.5 ${featured ? "bg-[#FFD600] text-[#0A0C10]" : "bg-[#0A0C10] text-white"} disabled:opacity-50`}
              >
                {isCurrent ? "Current" : price === 0 ? "Free" : loading === `plan:${p.code}` ? "Redirecting…" : "Choose"}
              </button>
            </div>
          );
        })}
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ top-ups</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
          {data.topups.map((t) => (
            <div key={t.id} className="bg-white p-5 flex flex-col">
              <Icon kind={t.kind} />
              <div className="mt-3 font-display font-semibold">{t.name}</div>
              <div className="text-2xl font-display font-bold mt-2">${t.amount.toFixed(2)}</div>
              <button onClick={() => checkout("topup", t.id)} disabled={loading === `topup:${t.id}`} data-testid={`buy-topup-${t.id}`} className="mt-3 bg-[#0A0C10] text-white font-bold py-2 disabled:opacity-50">{loading === `topup:${t.id}` ? "…" : "Buy"}</button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ recent payments</div>
        <div className="bg-white border border-neutral-200">
          {tx.length === 0 ? (
            <div className="text-sm text-neutral-500 p-6 text-center">No payments yet.</div>
          ) : tx.map((t) => (
            <div key={t.id} className="px-5 py-3 border-b border-neutral-100 last:border-0 flex items-center justify-between text-sm">
              <div>
                <div className="font-medium">{t.kind} · {t.package_id}</div>
                <div className="text-xs text-neutral-500">{fmtDateTime(t.created_at)}</div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-neutral-600">${t.amount.toFixed(2)} {t.currency.toUpperCase()}</span>
                <span className={`text-xs px-2 py-0.5 ${t.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-neutral-100 text-neutral-600"}`}>{t.payment_status}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Icon({ kind }) {
  if (kind === "topup_credits") return <Sparkles size={22} className="text-[#FFD600]" />;
  if (kind === "topup_storage") return <HardDrive size={22} className="text-[#002FA7]" />;
  if (kind === "topup_students") return <UsersIcon size={22} className="text-[#002FA7]" />;
  return <CreditCard size={22} />;
}
